<script
  setup
  lang="ts"
  generic="
    RowData extends DefaultRow,
    FilterRender extends Record<string, FilterRenderComponent<RowData>>,
    SortRender extends Record<string, SortRenderComponent<RowData>>,
    CellClass extends Record<string, CellClassInterface<RowData>>,
    HeaderClass extends Record<string, HeaderClassInterface<RowData>>,
    FilterType extends Record<string, FilterFn<RowData>>,
    SortType extends Record<string, SortFn<RowData>>
  "
>
  import {
    type CellClassInterface,
    type DefaultRow,
    type FilterFn,
    type FilterRenderComponent,
    type HeaderClassInterface,
    type SortFn,
    type SortRenderComponent,
    type TableProps,
    VTable,
  } from "@krainovsd/vue-ui";
  import { computed, ref } from "vue";
  import { CELL_RENDERS } from "./cellRenders";
  import { HEADER_CELL_RENDERS } from "./headerRenders";
  import { fixedColumns$ } from "./table.events";
  import type { TableCellRenders, TableHeaderRenders } from "./table.types";

  const props =
    defineProps<
      TableProps<
        RowData,
        TableCellRenders,
        TableHeaderRenders,
        FilterRender,
        SortRender,
        CellClass,
        HeaderClass,
        FilterType,
        SortType
      >
    >();

  const fixedColumns = ref<Set<string>>(new Set());

  fixedColumns$.useSubscribe((value) => {
    fixedColumns.value = new Set(value);
  });

  const columns = computed(() => {
    return props.columns.map((column) => {
      if (fixedColumns.value.has(column.key)) {
        return { ...column, leftFrozen: true };
      }
      return {
        ...column,
        leftFrozen: false,
      };
    });
  });
</script>

<template>
  <VTable
    :rows="props.rows"
    :columns="columns"
    :cell-renders="CELL_RENDERS"
    :header-renders="HEADER_CELL_RENDERS"
    :default-column-options="props.defaultColumnOptions"
    :get-row-id="props.getRowId"
    :get-sub-rows="props.getSubRows"
    :header-height="props.headerHeight"
    :virtual-rows="props.virtualRows"
    :virtual-row-size="props.virtualRowSize"
    :row-height="props.rowHeight"
    :with-total="props.withTotal"
  />
</template>

<style lang="scss" module></style>
